/* ==========================================================================
   Product Privacy & Data Protection Page Component
   Comprehensive Technical & Legal Privacy Architecture for Sound Event Detector
   Covers CS, EN, DE, ES, PT, AR (with strict RTL support)
   ========================================================================== */

import { getLocale } from '../locales/i18n.js';
import { getSvgIcon } from '../components/Icons.js';
import { exportLegalDocumentToPdf, PDF_BUTTON_LABELS } from '../utils/legalPdfExport.js';

import { productPrivacyData } from '../locales/legalTranslations.js';

export function renderProductPrivacyPage(onNavigate) {
  const container = document.createElement('div');
  container.className = 'sed-page';
  const lang = getLocale() || 'en';
  const data = productPrivacyData[lang] || productPrivacyData.en;
  const isRtl = lang === 'ar';
  const pdfBtnLabel = PDF_BUTTON_LABELS[lang] || PDF_BUTTON_LABELS.en;

  container.innerHTML = `
    <!-- Hero Header -->
    <section class="sed-section" style="background: linear-gradient(165deg, var(--blue-light) 0%, #ffffff 70%); padding: 4.5rem 0 3rem; direction: ${isRtl ? 'rtl' : 'ltr'};">
      <div class="sed-container">
        <div class="sed-section-header" style="max-width: 860px; margin: 0 auto 2rem; text-align: center;">
          <div class="sed-eyebrow" style="display: inline-flex; align-items: center; gap: 8px;">
            ${getSvgIcon('productPrivacy', 18, 'var(--blue-primary)')}
            <span>${data.eyebrow}</span>
          </div>
          <h1 class="sed-page-hero-title">${data.title}</h1>
          <p class="sed-section-desc">${data.subtitle}</p>
        </div>
      </div>
    </section>

    <!-- Main Content Section -->
    <section class="sed-section" style="background-color: #ffffff; padding: 2rem 0 5rem; direction: ${isRtl ? 'rtl' : 'ltr'}; text-align: ${isRtl ? 'right' : 'left'};">
      <div class="sed-container" style="max-width: 960px;">

        <!-- Metadata Header Card -->
        <div class="sed-card" style="padding: 1.75rem 2rem; margin-bottom: 2rem; background: #f8fafc; border: 1.5px solid var(--slate-line); border-radius: 12px; display: flex; justify-content: space-between; align-items: flex-start; flex-wrap: wrap; gap: 1rem;">
          <div>
            <div style="font-weight: 800; color: var(--navy-dark); font-size: 1.25rem; font-family: var(--font-heading); margin-bottom: 0.35rem;">
              JALUD Embedded s.r.o.
            </div>
            <div style="font-size: 0.92rem; color: var(--text-dark); margin-bottom: 0.3rem;">
              <strong>${data.companyDetails}</strong>
            </div>
            <div style="font-size: 0.88rem; color: var(--text-muted); line-height: 1.6;">
              Web: <a href="https://www.jalud.eu" target="_blank" rel="noopener noreferrer" style="color: var(--blue-primary); text-decoration: none; font-weight: 600;">www.jalud.eu</a> &bull; 
              E-mail: <span class="sed-obf-email" data-u="aW5mbw==" data-d="amFsdWQtZW1iZWRkZWQuY29t" data-t="aW5mb0BqYWx1ZC1lbWJlZGRlZC5jb20=" style="color: var(--blue-primary); text-decoration: none; font-weight: 600;"><span class="sed-obf-fallback">info [at] jalud-embedded.com</span></span>
            </div>
          </div>
          <div style="display: flex; flex-direction: row; align-items: center; gap: 0.75rem; flex-wrap: wrap;">
            <div style="font-size: 0.82rem; font-weight: 700; color: var(--blue-primary); background: #ffffff; padding: 0.45rem 0.9rem; border-radius: 6px; border: 1px solid var(--slate-line); white-space: nowrap;">
              ${data.version}
            </div>
            <button class="sed-btn sed-btn-outline sed-download-pdf-btn" id="sed-download-pdf-btn" style="font-size: 0.82rem; padding: 0.45rem 0.9rem; display: inline-flex; align-items: center; gap: 6px; cursor: pointer; border-radius: 6px; background: #ffffff; color: var(--blue-primary); border-color: var(--blue-primary); font-weight: 700; transition: all 0.2s; white-space: nowrap;" onmouseover="this.style.background='var(--blue-ultra-light)'" onmouseout="this.style.background='#ffffff'">
              ${getSvgIcon('download', 14, 'var(--blue-primary)')}
              <span>${pdfBtnLabel}</span>
            </button>
          </div>
        </div>

        <!-- Quick Table of Contents -->
        <div style="background: var(--blue-light); border: 1px solid var(--blue-primary); border-radius: 12px; padding: 1.5rem; margin-bottom: 2.5rem;">
          <div style="font-size: 0.85rem; font-weight: 800; color: var(--navy-dark); text-transform: uppercase; letter-spacing: 0.5px; margin-bottom: 0.75rem;">
            📑 ${data.tocTitle}
          </div>
          <div style="display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 0.6rem; font-size: 0.92rem;">
            ${data.sections.map((item) => `
              <a href="#${item.id}" class="sed-terms-nav" style="color: var(--navy-dark); font-weight: 600; text-decoration: none; display: inline-flex; align-items: center; gap: 8px; transition: color 0.2s;" onmouseover="this.style.color='var(--blue-primary)'" onmouseout="this.style.color='var(--navy-dark)'">
                <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 22px; height: 22px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.78rem; font-weight: 800; flex-shrink: 0;">${item.num}</span>
                <span>${item.title}</span>
              </a>
            `).join('')}
          </div>
        </div>

        <!-- Main Document Body -->
        <div class="sed-card" style="padding: 2.75rem; line-height: 1.8; color: var(--text-dark); background: #ffffff; border: 1.5px solid var(--slate-line); border-radius: 16px; box-shadow: 0 4px 20px rgba(0,0,0,0.03);">
          
          <!-- Key Callout: Privacy by Design -->
          <div style="background: #f0fdf4; border-left: ${isRtl ? 'none' : '4px solid #16a34a'}; border-right: ${isRtl ? '4px solid #16a34a' : 'none'}; padding: 1.25rem 1.5rem; border-radius: ${isRtl ? '8px 0 0 8px' : '0 8px 8px 0'}; margin-bottom: 2.5rem; font-size: 1.02rem; color: #14532d; line-height: 1.7;">
            <div style="font-weight: 800; font-size: 1.05rem; margin-bottom: 0.35rem; color: #15803d; display: flex; align-items: center; gap: 8px;">
              <span>🛡️</span> <span>${data.introBadge}</span>
            </div>
            ${data.introText}
          </div>

          <!-- The Sections -->
          <div style="display: flex; flex-direction: column; gap: 2rem;">
            ${data.sections.map(sec => `
              <div id="${sec.id}" style="scroll-margin-top: 100px; border-bottom: 1px solid var(--slate-line); padding-bottom: 1.75rem;">
                <h2 style="font-family: var(--font-heading); font-size: 1.25rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 0.75rem; display: flex; align-items: center; gap: 10px;">
                  <span style="background: var(--blue-ultra-light); color: var(--blue-primary); width: 28px; height: 28px; border-radius: 50%; display: inline-flex; align-items: center; justify-content: center; font-size: 0.85rem; font-weight: 800; flex-shrink: 0;">
                    ${sec.num}
                  </span>
                  <span>${sec.title}</span>
                </h2>
                <div style="font-size: 1rem; line-height: 1.8; color: var(--text-dark);">
                  ${sec.text}
                </div>
              </div>
            `).join('')}
          </div>

          <!-- Cross-Links to Other Legal Documents -->
          <div style="margin-top: 3.5rem; padding-top: 2rem; border-top: 2px solid var(--blue-light);">
            <h3 style="font-family: var(--font-heading); font-size: 1.15rem; font-weight: 800; color: var(--navy-dark); margin-bottom: 1.25rem;">
              ${data.crossLinksTitle}
            </h3>
            <div style="display: grid; grid-template-columns: repeat(auto-fit, minmax(240px, 1fr)); gap: 1rem;">
              ${data.crossLinks.map(link => `
                <a href="/${link.route}" data-route="${link.route}" class="sed-card" style="padding: 1.25rem; border: 1.5px solid var(--slate-line); border-radius: 10px; text-decoration: none; color: inherit; transition: all 0.2s; background: #fafafa; display: block;" onmouseover="this.style.borderColor='var(--blue-primary)'; this.style.transform='translateY(-2px)'" onmouseout="this.style.borderColor='var(--slate-line)'; this.style.transform='none'">
                  <div style="font-weight: 700; color: var(--blue-primary); font-size: 0.95rem; margin-bottom: 0.35rem;">
                    ${link.name} &rarr;
                  </div>
                  <div style="font-size: 0.85rem; color: var(--text-muted); line-height: 1.4;">
                    ${link.desc}
                  </div>
                </a>
              `).join('')}
            </div>
          </div>

        </div>
      </div>
    </section>
  `;

  // Attach internal routing handlers to cross links and inline links
  container.querySelectorAll('a[data-route]').forEach(link => {
    link.addEventListener('click', (e) => {
      e.preventDefault();
      const route = link.getAttribute('data-route');
      if (typeof onNavigate === 'function') {
        onNavigate(route);
      }
    });
  });

  // Attach PDF export button handler
  const pdfBtn = container.querySelector('#sed-download-pdf-btn');
  if (pdfBtn) {
    pdfBtn.addEventListener('click', (e) => {
      e.preventDefault();
      exportLegalDocumentToPdf('productPrivacy', lang, pdfBtn);
    });
  }

  return container;
}
